import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import axiosInstance from '../config/axios';
import Loading from '../components/Loading';
import ErrorMessage from '../components/ErrorMessage';
import { useTranslation } from '../hooks/useTranslation';

interface Product {
  id: string;
  name_en: string;
  name_zh: string;
  description_en: string;
  description_zh: string;
  price_per_kg: number;
  image_filename: string | null;
  is_active: boolean;
}

const ProductsPage: React.FC = () => {
  const navigate = useNavigate();
  const { t, language } = useTranslation();

  const { data: products, isLoading, error } = useQuery<Product[]>({
    queryKey: ['products'],
    queryFn: async () => {
      const response = await axiosInstance.get('/api/products');
      return response.data;
    },
  });

  if (isLoading) {
    return <Loading />;
  }

  if (error) {
    return <ErrorMessage message={t('products.loadingError')} />;
  }

  return (
    <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
      <h1>{t('products.title')}</h1>
      <p style={{ marginBottom: '30px', color: '#666' }}>
        {t('products.subtitle')}
      </p>

      {products && products.length > 0 ? (
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', 
          gap: '20px' 
        }}>
          {products.map(product => (
            <div
              key={product.id}
              onClick={() => navigate(`/products/${product.id}`)}
              style={{
                padding: '20px',
                border: '1px solid #ddd',
                borderRadius: '8px',
                cursor: 'pointer',
                backgroundColor: 'white',
                transition: 'transform 0.2s, box-shadow 0.2s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              {product.image_filename && (
                <img
                  src={`/images/${product.image_filename}`}
                  alt={language === 'zh' ? product.name_zh : product.name_en}
                  style={{
                    width: '100%',
                    height: '200px',
                    objectFit: 'cover',
                    borderRadius: '8px',
                    marginBottom: '15px',
                  }}
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
              )}
              <h2 style={{ margin: '0 0 10px 0', fontSize: '24px' }}>
                {language === 'zh' ? product.name_zh : product.name_en}
              </h2>
              <p style={{ margin: '10px 0', color: '#666', minHeight: '60px' }}>
                {language === 'zh' ? product.description_zh : product.description_en}
              </p>
              <p style={{ margin: '15px 0 0 0', fontSize: '20px', fontWeight: 'bold', color: '#007bff' }}>
                €{product.price_per_kg.toFixed(2)} {t('products.perKg')} <span style={{ fontSize: '14px', fontWeight: 'normal', color: '#666' }}>({t('products.inclVat')})</span>
              </p>
              <button
                style={{
                  marginTop: '15px',
                  padding: '10px 20px',
                  backgroundColor: '#007bff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  fontSize: '16px',
                  cursor: 'pointer',
                  width: '100%',
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/products/${product.id}`);
                }}
              >
                {t('products.viewDetails')}
              </button>
            </div>
          ))}
        </div>
      ) : (
        <p style={{ textAlign: 'center', color: '#666', marginTop: '40px' }}>
          {t('products.noProducts')}
        </p>
      )}
    </div>
  );
};

export default ProductsPage;
